// Row-level filter for the FilterChipBar selections (Status / Owner /
// RAG / Due / Include-archived) and the search box. Runs after
// groupDeliverables and after applySummaryFilter so parent/child
// grouping is preserved: a parent survives when at least one child
// passes the predicate, and the matching subset replaces `children`
// while the original is preserved on `allChildren` for the
// OccurrenceStrip history.
//
// Status uses the DERIVED status (so the synthetic 'overdue' option
// matches), and rag uses the EFFECTIVE rag (so overdue items match
// 'red' even without a stored rating). Search is parsed via searchQuery
// (AND of bare words, "quoted phrases", -exclusions) and matched against
// `name` and `clauseReference` case-insensitively. The raw query is
// parsed once per applyChipFilters call and threaded into the matcher.

import { deriveDeliverableStatus } from "./deriveDeliverableStatus";
import { deriveEffectiveRag } from "./deriveEffectiveRag";
import {
  parseSearchQuery,
  matchesSearchQuery,
  type ParsedSearchQuery,
} from "./searchQuery";
import type { ChildRow, ParentRow, Row } from "./groupDeliverables";
import {
  UNATTACHED_TOKEN,
  UNLINKED_MILESTONE_TOKEN,
  type FilterChipBarFilters,
} from "./FilterChipBar";
import type { Deliverable } from "../../../../types";

const DAY_MS = 86_400_000;

function startOfUTCDay(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function isoToUTCDay(iso: string): number | null {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function matchesStatus(
  d: Deliverable,
  selected: FilterChipBarFilters["status"],
  today: Date,
): boolean {
  if (selected.length === 0) return true;
  const derived = deriveDeliverableStatus(
    d.status,
    d.dueDate,
    today,
    d.submittedAt,
  );
  return selected.includes(derived);
}

function matchesOwner(
  d: Deliverable,
  selected: FilterChipBarFilters["owner"],
): boolean {
  if (selected.length === 0) return true;
  return selected.includes(d.owner);
}

function matchesRag(
  d: Deliverable,
  selected: FilterChipBarFilters["rag"],
  today: Date,
): boolean {
  if (selected.length === 0) return true;
  const effective = deriveEffectiveRag(d, today);
  if (effective === null) return selected.includes("not-set");
  return selected.includes(effective);
}

function matchesDue(
  d: Deliverable,
  selected: FilterChipBarFilters["due"],
  today: Date,
): boolean {
  if (selected.length === 0) return true;
  if (!d.dueDate) return false;
  const due = isoToUTCDay(d.dueDate);
  if (due === null) return false;
  const todayMs = startOfUTCDay(today);
  for (const bucket of selected) {
    if (bucket === "overdue" && due < todayMs) return true;
    if (
      bucket === "this-week" &&
      due >= todayMs &&
      due <= todayMs + 7 * DAY_MS
    ) {
      return true;
    }
    if (
      bucket === "next-30-days" &&
      due > todayMs + 7 * DAY_MS &&
      due <= todayMs + 30 * DAY_MS
    ) {
      return true;
    }
    if (bucket === "future" && due > todayMs + 30 * DAY_MS) return true;
  }
  return false;
}

function matchesSow(
  d: Deliverable,
  selected: FilterChipBarFilters["sow"],
): boolean {
  if (selected.length === 0) return true;
  const wantsUnattached = selected.includes(UNATTACHED_TOKEN);
  if (wantsUnattached && d.sowId == null) return true;
  if (d.sowId != null && selected.includes(d.sowId)) return true;
  return false;
}

function matchesMilestone(
  d: Deliverable,
  selected: FilterChipBarFilters["milestone"],
): boolean {
  if (selected.length === 0) return true;
  const wantsUnlinked = selected.includes(UNLINKED_MILESTONE_TOKEN);
  if (wantsUnlinked && d.milestoneId == null) return true;
  if (d.milestoneId != null && selected.includes(d.milestoneId)) return true;
  return false;
}

function matchesSearch(d: Deliverable, query: ParsedSearchQuery): boolean {
  return matchesSearchQuery(d, query);
}

function matchesHasFailedAcceptance(
  d: Deliverable,
  hasFailedAcceptance: boolean,
): boolean {
  if (!hasFailedAcceptance) return true;
  return (d.failedAcceptanceCount ?? 0) > 0;
}

// Not-accepted quick filter: when on, hide deliverables whose DERIVED
// status is `accepted`. `accepted` is sticky in deriveDeliverableStatus
// (an accepted-but-late item still derives `accepted`), so this keys off
// the derived status with no overlay edge cases — every other state
// (incl. conditionally-accepted, closed, rejected, overdue, late) passes.
function matchesNotAccepted(
  d: Deliverable,
  notAccepted: boolean,
  today: Date,
): boolean {
  if (!notAccepted) return true;
  const derived = deriveDeliverableStatus(
    d.status,
    d.dueDate,
    today,
    d.submittedAt,
  );
  return derived !== "accepted";
}

function matches(
  d: Deliverable,
  filters: FilterChipBarFilters,
  query: ParsedSearchQuery,
  today: Date,
): boolean {
  return (
    matchesStatus(d, filters.status, today) &&
    matchesOwner(d, filters.owner) &&
    matchesRag(d, filters.rag, today) &&
    matchesDue(d, filters.due, today) &&
    matchesSow(d, filters.sow) &&
    matchesMilestone(d, filters.milestone) &&
    matchesHasFailedAcceptance(d, filters.hasFailedAcceptance) &&
    matchesNotAccepted(d, filters.notAccepted, today) &&
    matchesSearch(d, query)
  );
}

export function applyChipFilters(
  rows: Row[],
  filters: FilterChipBarFilters,
  search: string,
  today: Date = new Date(),
): Row[] {
  const noChipFilters =
    filters.status.length === 0 &&
    filters.owner.length === 0 &&
    filters.rag.length === 0 &&
    filters.due.length === 0 &&
    filters.sow.length === 0 &&
    filters.milestone.length === 0 &&
    !filters.hasFailedAcceptance &&
    !filters.notAccepted;
  // Parse the raw query once. A query that parses to zero terms (empty,
  // whitespace, a lone '-', empty quotes) counts as "no search" so the
  // existing no-op fast path still applies.
  const query = parseSearchQuery(search);
  const noSearch = query.include.length === 0 && query.exclude.length === 0;
  if (noChipFilters && noSearch) return rows;

  const result: Row[] = [];
  for (const row of rows) {
    if (row.kind === "one-off" || row.kind === "orphan") {
      // Orphan rows behave like one-offs for chip filters + search:
      // the predicate runs against the orphan's own deliverable.
      if (matches(row.deliverable, filters, query, today)) result.push(row);
      continue;
    }
    if (row.kind === "parent") {
      // A parent survives if EITHER its own metadata matches the
      // search/owner predicate (so users can find a recurring
      // schedule by name even when no child currently satisfies the
      // filter) OR at least one child does.
      const parentMatches = matches(row.deliverable, filters, query, today);
      const matchingChildren = row.children.filter((c) =>
        matches(c.deliverable, filters, query, today),
      );
      if (!parentMatches && matchingChildren.length === 0) continue;
      // Children always narrow to the matching subset
      // — even when the parent's own metadata satisfies the chip
      // (e.g. owner=supplier hits both the parent and only some
      // children). Mirrors applySummaryFilter so the card-driven and
      // chip-driven filter paths produce the same children. The
      // OccurrenceStrip still renders the full schedule history via
      // `allChildren`.
      const filteredParent: ParentRow = {
        kind: "parent",
        deliverable: row.deliverable,
        children: matchingChildren,
        allChildren: row.children,
      };
      result.push(filteredParent);
      continue;
    }
    const childRow = row as ChildRow;
    if (matches(childRow.deliverable, filters, query, today)) {
      result.push(childRow);
    }
  }
  return result;
}
